import { useSTKContext } from '@/context/context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  BundleAvailablePriceRequestBody,
  BundleDetails,
  BundleMissingVariantsResponse,
  BundleStatistics,
  BundleStoreCount,
  BundleUpchargeData,
  BundleUpchargeStoreData,
  CreateLinkedBundles,
  DeactivateBundleRequestBody,
  EditBundlePriceRequestBody,
  EditLinkedBundlePriceRequestBody,
  NavigationCode,
  StoreGroup,
} from '../../types/bundles.types';
import { Freeze } from '../../types/freeze.types';
import {
  CreateMaintenanceFreezeRequestBody,
  EditMaintenanceFreezeRequestBody,
  MaintenanceFreezeResponse,
} from '../../types/maintenanceFreeze.types';
import {
  CreateNationalProgramRequestBody,
  EditNationalProgramRequestBody,
  EquityStoreBundleSettings,
  EquityStoreTestingRequest,
  EquityStoreTestingResult,
  NationalProgramDetailsResponse,
  NationalProgramResponse,
} from '../../types/nationalProgram.types';

export const useBundlesForGroup = (enabled: boolean, groupId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<BundleDetails[]>>({
    queryKey: ['bundles', groupId],
    queryFn: () => apiClient.get<BundleDetails[]>(`/bundles`, { params: { groupId } }),
    enabled,
  });
};

export const useBundles = (enabled: boolean) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<BundleDetails[]>>({
    queryKey: ['bundles'],
    queryFn: () => apiClient.get<BundleDetails[]>('/bundles'),
    enabled,
  });
};

export const useBundleDetails = (enabled: boolean, bundleId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<BundleDetails>>({
    queryKey: ['bundle', bundleId],
    queryFn: () => apiClient.get<BundleDetails>(`/bundles/${bundleId}`),
    enabled,
  });
};

export const useBundleUpchargeStoreGroups = (enabled: boolean) => {
  const { apiClient } = useSTKContext();
  return useQuery<BundleUpchargeData>({
    queryKey: ['bundle-upcharge-store-groups'],
    queryFn: async () => {
      const response = await apiClient.get<BundleUpchargeData>('/bundle-upcharges/store-groups');
      return response.data;
    },
    enabled,
  });
};

export const useBundleUpchargeStores = (enabled: boolean) => {
  const { apiClient } = useSTKContext();
  return useQuery<BundleUpchargeStoreData>({
    queryKey: ['bundle-upcharge-stores'],
    queryFn: async () => {
      const response = await apiClient.get<BundleUpchargeStoreData>('/bundle-upcharges/stores');
      return response.data;
    },
    enabled,
  });
};

export const useStoreGroups = (enabled: boolean, bundleId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<StoreGroup[]>>({
    queryKey: ['bundle-groups', bundleId],
    queryFn: () => apiClient.get<StoreGroup[]>(`/bundles/${bundleId}/groups`),
    enabled,
  });
};

export const useMissingVariants = (enabled: boolean, bundleId: string, groupId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<BundleMissingVariantsResponse>>({
    queryKey: ['bundle-missing-variants', bundleId, groupId],
    queryFn: () =>
      apiClient.get<BundleMissingVariantsResponse>(`/bundles/${bundleId}/groups/${groupId}/missing-variants`),
    enabled: enabled && !!bundleId && !!groupId,
  });
};

export const useBundleGroupsPointPrice = (
  enabled: boolean,
  bundleId: string | undefined,
  bundleGroups: StoreGroup[],
) => {
  const { apiClient } = useSTKContext();
  return useQuery<StoreGroup[]>({
    queryKey: ['bundle-groups-price-point', bundleId],
    queryFn: async () => {
      const response = await apiClient.post<StoreGroup[]>(`/bundle-groups-price/${bundleId}`, bundleGroups);
      return response.data;
    },
    enabled,
  });
};

export const useEditBundlePrice = (bundleId: string) => {
  const { apiClient } = useSTKContext();
  return useMutation((requestBody: EditBundlePriceRequestBody) =>
    apiClient.put(`/bundles/${bundleId}/price`, requestBody),
  );
};

export const useEditLinkedBundlePrice = (bundleId: string) => {
  const { apiClient } = useSTKContext();
  return useMutation((requestBody: EditLinkedBundlePriceRequestBody) =>
    apiClient.put(`/bundles/${bundleId}/linked-price`, requestBody),
  );
};

export const useDeleteAvailablePrice = () => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bundleId, price }: { bundleId: string; price: string }) =>
      apiClient.delete(`/bundles/${bundleId}/available-price/${price}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['bundleDetails']);
    },
  });
};

export const useEditBundle = (bundleId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestBody: {
      isVisible?: boolean;
      recommendedPrices?: string[];
      isLockRedeemableOnline?: boolean;
      lockRedeemableOnline?: boolean;
      isLockVisibleOnline?: boolean;
      lockVisibleOnline?: boolean;
      upChargePrice?: string;
    }) => apiClient.put(`/bundles/${bundleId}`, requestBody),
    onSuccess: () => {
      queryClient.invalidateQueries(['bundle', bundleId]);
      queryClient.invalidateQueries(['bundles']);
    },
  });
};

export const useUpdateBundleVisibility = (bundleId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ isVisible }: { isVisible: boolean }) =>
      apiClient.put(`/bundles/${bundleId}/visibility`, { isVisible }),
    onSuccess: () => {
      queryClient.invalidateQueries(['bundle', bundleId]);
      queryClient.invalidateQueries(['bundles']);
    },
  });
};

export const useUpdateBundleLock = (bundleId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bundleCode }: { bundleCode: string }) =>
      apiClient.put(`/bundles/${bundleId}/toggle-lock`, { bundleCode }),
    onSuccess: () => {
      queryClient.invalidateQueries(['bundle', bundleId]);
      queryClient.invalidateQueries(['bundles']);
    },
  });
};

export const useCreateLinkedBundles = () => {
  const { apiClient } = useSTKContext();
  return useMutation((requestBody: CreateLinkedBundles) => apiClient.put('/bundles/linked-bundles', requestBody));
};

export const useDeactivateBundle = (bundleId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ allGroups, groups, updateChosenSettingsOnly }: DeactivateBundleRequestBody) =>
      apiClient.put(
        `/bundles/${bundleId}/deactivate`,
        { groups, ...(updateChosenSettingsOnly ? { updateChosenSettingsOnly } : {}) },
        { params: { allGroups } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(['bundles']);
    },
  });
};

export const useUnlinkBundles = () => {
  const { apiClient } = useSTKContext();
  return useMutation({
    mutationFn: ({ bundleName }: { bundleName: string }) => apiClient.delete(`/bundles/linked-bundles/${bundleName}`),
  });
};

export const useNationalProgramCurrent = (enabled: boolean, bundleId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<NationalProgramResponse>>({
    queryKey: ['national-programs', bundleId],
    queryFn: () => apiClient.get<NationalProgramResponse>(`/national-programs/current`, { params: { bundleId } }),
    enabled,
  });
};

export const useMaintenanceFreezeDetails = (enabled: boolean, bundleId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<MaintenanceFreezeResponse>>({
    queryKey: ['maintenance-freezes', bundleId],
    queryFn: () => apiClient.get<MaintenanceFreezeResponse>(`/maintenance-freezes`, { params: { bundleId } }),
    enabled,
  });
};

export const useCreateNationalProgram = () => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (NationalProgram: CreateNationalProgramRequestBody) =>
      apiClient.post('/national-programs', NationalProgram),
    onSuccess: () => {
      queryClient.invalidateQueries(['national-programs']);
    },
  });
};

export const useCreateMaintenanceFreeze = () => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (maintenanceFreeze: CreateMaintenanceFreezeRequestBody) =>
      apiClient.post('/maintenance-freezes', maintenanceFreeze),
    onSuccess: () => {
      queryClient.invalidateQueries(['maintenance-freezes']);
    },
  });
};

export const useEditNationalProgram = (nationalProgramId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestBody: EditNationalProgramRequestBody) =>
      apiClient.put(`/national-programs/${nationalProgramId}`, requestBody),
    onSuccess: () => {
      queryClient.invalidateQueries(['national-programs']);
    },
  });
};

export const useEditMaintenanceFreeze = (maintenanceId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestBody: EditMaintenanceFreezeRequestBody) =>
      apiClient.put(`/maintenance-freezes/${maintenanceId}`, requestBody),
    onSuccess: () => {
      queryClient.invalidateQueries(['maintenance-freezes']);
    },
  });
};

export const useEditNationalProgramPriceSelection = (nationalProgramId: string, bundleId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestBody: { bundleId: string; groups: { groupId: string; chosenPrice: string | null }[] }) =>
      apiClient.put(`/national-programs/${nationalProgramId}/groups`, requestBody),
    onSuccess: () => {
      queryClient.invalidateQueries(['national-programs', bundleId]);
    },
  });
};

export const useAllFreezesForBundle = (enabled: boolean, bundleId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<Freeze[]>>({
    queryKey: ['freezes', bundleId],
    queryFn: () => apiClient.get<Freeze[]>(`/freezes`, { params: { bundleId } }),
    enabled,
  });
};

export const useDeleteNationalProgram = () => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/national-programs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['national-programs']);
      queryClient.invalidateQueries(['freezes']);
    },
  });
};

export const useDeleteMaintenanceFreeze = () => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/maintenance-freezes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['maintenance-freezes']);
      queryClient.invalidateQueries(['freezes']);
    },
  });
};

export const useEquityStoreBundleSettings = (enabled: boolean, nationalProgramId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<EquityStoreBundleSettings>>({
    queryKey: ['equity-store-bundle-settings', nationalProgramId],
    queryFn: () =>
      apiClient.get<EquityStoreBundleSettings>(`/national-programs/${nationalProgramId}/equity-store-bundle-settings`),
    enabled,
  });
};

export const useConfigureEquityStoreTesting = () => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ nationalProgramId, body }: { nationalProgramId: string; body: EquityStoreTestingRequest }) =>
      apiClient.put<EquityStoreTestingResult>(
        `/national-programs/${nationalProgramId}/update-equity-store-bundle-settings`,
        body,
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(['equity-store-bundle-settings', variables.nationalProgramId]);
      queryClient.invalidateQueries(['freezes']);
    },
  });
};

export const useNationalProgramDetails = (enabled: boolean, nationalProgramId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<NationalProgramDetailsResponse>>({
    queryKey: ['national-program', nationalProgramId],
    queryFn: () =>
      apiClient.get<NationalProgramDetailsResponse>(`/national-programs/${nationalProgramId}/details`, {
        params: { nationalProgramId },
      }),
    enabled,
  });
};

export const useFreezeBundle = (enabled: boolean, bundleId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<boolean>>({
    queryKey: ['bundle-freeze', bundleId],
    queryFn: () => apiClient.get<boolean>(`/bundle-freeze/current`, { params: { bundleId } }),
    enabled,
  });
};

export const useAddAvailablePrice = (bundleId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bundleAvailablePrice: BundleAvailablePriceRequestBody) =>
      apiClient.post(`/bundles/${bundleId}/available-price`, bundleAvailablePrice),
    onSuccess: () => {
      queryClient.invalidateQueries(['bundleDetails']);
    },
  });
};

export const useBundleStatistics = (enabled: boolean, bundleId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<BundleStatistics>>({
    queryKey: ['bundle-statistics', bundleId],
    queryFn: () => apiClient.get<BundleStatistics>(`/bundles/${bundleId}/statistics`),
    enabled,
  });
};

export const useNavigationCodes = (enabled: boolean, bundleId: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<NavigationCode[]>({
    queryKey: ['view-navigation-code', bundleId],
    queryFn: async () => {
      const response = await apiClient.get<NavigationCode[]>(`/bundles/${bundleId}/viewNavCode`);
      return response.data;
    },
    enabled,
  });
};

export const useAddNavigationCodes = (enabled: boolean, bundleId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestBody: NavigationCode) => apiClient.post(`/bundles/${bundleId}/addNavCode`, requestBody),
    onSuccess: () => {
      queryClient.invalidateQueries(['view-navigation-code']);
    },
  });
};

export const useBundleUpchargePriceDeviations = (
  enabled: boolean,
  type: string | undefined,
  groupId: string | undefined,
) => {
  const { apiClient } = useSTKContext();
  return useQuery({
    queryFn: async () => {
      const response = await apiClient.get(`/bundle-upcharges/price-deviations/${type}/${groupId}`);
      return response.data;
    },
    enabled,
  });
};
export const useDeleteNavigationCodes = (bundleId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => apiClient.delete(`/bundles/${bundleId}/deleteNavCode/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['view-navigation-code']);
    },
  });
};

export const useBundleStoresCount = ({
  bundleId,
  enabled,
  pricePoint,
}: {
  bundleId: string;
  enabled: boolean;
  pricePoint: string;
}) => {
  const { apiClient } = useSTKContext();
  return useQuery<BundleStoreCount>({
    queryKey: ['bundle-store-count', bundleId],
    queryFn: async () => {
      const response = await apiClient.get<BundleStoreCount>(`/bundles/${bundleId}/stores`, {
        params: { pricePoint },
      });
      return response.data;
    },
    enabled,
  });
};
